import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { setCurrentSignupFlow, trackSignupEvent, trackSignupEventOnce } from '../../lib/signupAnalytics';
import { isPasswordStrongEnough } from '../../lib/passwordStrength';
import PasswordRequirements from '../../components/auth/PasswordRequirements';
import SignupForm from '../../components/auth/SignupForm';
import {
  checkSignupEmailAvailable,
  signupEmailUnavailableMessage,
} from '../../lib/signupEmailVerification';

/** Brand guidelines — keep in sync with SignupNexus */
const NEXUS_TEAL = '#21C7D1';
const HORIZON_BLUE = '#3B82F6';
const BRAND_GRADIENT = `linear-gradient(135deg, ${NEXUS_TEAL} 0%, ${HORIZON_BLUE} 100%)`;
const BRAND_CTA_SHADOW = '0 8px 30px rgba(33,199,209,0.28), 0 8px 30px rgba(59,130,246,0.22)';
const HERO_GLOW =
  'radial-gradient(ellipse at center, rgba(33,199,209,0.28) 0%, rgba(59,130,246,0.12) 38%, transparent 70%)';

type FlowStep = 'email' | 'password' | 'signup';

type LocationState = {
  email?: string;
  startAt?: 'email' | 'password';
};

/**
 * Signup-nexus account steps — separate from the marketing landing.
 * Email is always captured in funnel analytics before password / SignupForm.
 */
export default function SignupNexusEmail() {
  const navigate = useNavigate();
  const location = useLocation();
  const incoming = (location.state || {}) as LocationState;

  const [step, setStep] = useState<FlowStep>(
    incoming.startAt === 'password' && incoming.email ? 'password' : 'email',
  );
  const [email, setEmail] = useState(() => (incoming.email || '').trim().toLowerCase());
  const [password, setPassword] = useState('');
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [signupSeed, setSignupSeed] = useState<{ email: string; password: string } | null>(null);
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    setCurrentSignupFlow('nexus');
    trackSignupEventOnce('nexus:page_view:email', {
      eventType: 'page_view',
      step: 'email',
      flow: 'nexus',
    });
    // Trigger enter animation on next frame so CSS transition runs.
    const id = window.requestAnimationFrame(() => setEntered(true));
    return () => window.cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    if (step === 'signup') return;
    trackSignupEventOnce(`nexus:step_view:${step}`, {
      eventType: 'step_view',
      step,
      flow: 'nexus',
    });
  }, [step]);

  useEffect(() => {
    if (step !== 'email') return;
    window.setTimeout(() => document.getElementById('nexus-email')?.focus(), 320);
  }, [step]);

  useEffect(() => {
    if (step !== 'password') return;
    window.setTimeout(() => document.getElementById('nexus-password')?.focus(), 80);
  }, [step]);

  const handleEmailContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const normalized = email.trim().toLowerCase();
    if (!normalized || !/\S+@\S+\.\S+/.test(normalized)) {
      setError('Enter a valid email.');
      return;
    }

    setSubmitting(true);
    try {
      const availability = await checkSignupEmailAvailable(normalized);
      if (availability.available === false) {
        trackSignupEvent({
          eventType: 'error',
          step: 'email',
          flow: 'nexus',
          data: {
            reason: availability.reason === 'team_member' ? 'team_member_email' : 'email_in_use',
            email: normalized,
          },
        });
        setError(signupEmailUnavailableMessage(availability.reason));
        return;
      }
      if (!availability.ok && availability.error && availability.error !== 'throttled') {
        setError('Could not verify that email. Please try again.');
        return;
      }

      setEmail(normalized);
      trackSignupEventOnce(`nexus:step_complete:email:${normalized}`, {
        eventType: 'step_complete',
        step: 'email',
        flow: 'nexus',
        data: { email: normalized },
      });
      setStep('password');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePasswordContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const normalized = email.trim().toLowerCase();
    if (!normalized || !/\S+@\S+\.\S+/.test(normalized)) {
      setError('Enter a valid email.');
      setStep('email');
      return;
    }
    if (!isPasswordStrongEnough(password)) {
      setError('Password doesn’t meet the requirements below.');
      return;
    }

    setSubmitting(true);
    try {
      // Re-check in case they skipped the email screen (hero → password).
      const availability = await checkSignupEmailAvailable(normalized);
      if (availability.available === false) {
        trackSignupEvent({
          eventType: 'error',
          step: 'email',
          flow: 'nexus',
          data: {
            reason: availability.reason === 'team_member' ? 'team_member_email' : 'email_in_use',
            email: normalized,
          },
        });
        setError(signupEmailUnavailableMessage(availability.reason));
        setStep('email');
        return;
      }
      if (!availability.ok && availability.error && availability.error !== 'throttled') {
        setError('Could not verify that email. Please try again.');
        setStep('email');
        return;
      }

      sessionStorage.setItem(
        'signup_nexus_credentials',
        JSON.stringify({ email: normalized, password, marketingOptIn }),
      );
      trackSignupEvent({
        eventType: 'step_complete',
        step: 'password',
        flow: 'nexus',
        data: { email: normalized, marketing_opt_in: marketingOptIn },
      });
      setSignupSeed({ email: normalized, password });
      setStep('signup');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen text-white relative overflow-x-hidden nexus-email-page"
      style={{
        background: '#050507',
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        opacity: entered ? 1 : 0.96,
        transform: entered ? 'translateX(0)' : 'translateX(100%)',
      }}
    >
      <div
        className="pointer-events-none absolute left-1/2 top-[80px] -translate-x-1/2 h-[420px] w-[720px] rounded-full"
        style={{ background: HERO_GLOW }}
      />

      <div className="relative mx-auto w-full max-w-[560px] px-5 pt-10 pb-16 sm:pt-14">
        <div className="flex justify-center mb-8">
          <div className="inline-flex items-center gap-2.5">
            <img
              src="/nexus-connect-icon.png"
              alt=""
              className="h-10 w-10 sm:h-12 sm:w-12 rounded-[10px] object-cover select-none"
              draggable={false}
              aria-hidden
            />
            <img
              src="/Nexus%20Full%20Logo%20(512%20x%20150%20px).png"
              alt="Nexus"
              className="h-10 sm:h-12 w-auto select-none"
              draggable={false}
            />
          </div>
        </div>

        {step !== 'signup' && (
          <div className="mb-8 text-center">
            <h1 className="text-[26px] sm:text-[30px] font-extrabold leading-tight tracking-tight text-white">
              {step === 'email' ? 'What’s your email?' : 'Create your password'}
            </h1>
            <p className="mt-2 text-[14px] sm:text-[15px] text-white/55">
              {step === 'email'
                ? 'We’ll use this to set up your free trial.'
                : 'Almost there — then we’ll verify your email.'}
            </p>
          </div>
        )}

        <div className="flex justify-center">
          {step === 'signup' && signupSeed ? (
            <SignupForm
              key={`${signupSeed.email}:${signupSeed.password.length}`}
              initialEmail={signupSeed.email}
              initialPassword={signupSeed.password}
              autoStartVerification
            />
          ) : step === 'email' ? (
            <form
              onSubmit={handleEmailContinue}
              className="w-full rounded-2xl bg-white px-5 py-6 sm:px-7 sm:py-7 shadow-[0_8px_40px_rgba(0,0,0,0.35)] border border-white/80"
            >
              <label className="block text-[13px] font-semibold text-gray-900 mb-1.5" htmlFor="nexus-email">
                Email
              </label>
              <input
                id="nexus-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (error) setError(null);
                }}
                placeholder="you@company.com"
                className="w-full h-11 px-3 rounded-xl border border-gray-300 bg-white text-[15px] text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#3B82F6]/40 focus:border-[#3B82F6]"
              />

              {error && (
                <p className="mt-3 text-[13px] text-red-600 font-medium" role="alert">
                  {error}
                  {error.toLowerCase().includes('already exists') && (
                    <>
                      {' '}
                      <a href="/login" className="underline text-gray-800 font-semibold">
                        Log in
                      </a>
                    </>
                  )}
                </p>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="mt-5 w-full h-12 rounded-full text-white text-[16px] font-semibold transition-opacity hover:opacity-95 disabled:opacity-60"
                style={{ background: BRAND_GRADIENT, boxShadow: BRAND_CTA_SHADOW }}
              >
                {submitting ? 'Checking…' : 'Continue'}
              </button>

              <p className="mt-3 text-center text-[11px] leading-snug text-gray-500">
                By clicking &quot;Continue&quot; you agree to Nexus&apos;s{' '}
                <a
                  href="https://www.nexusbookai.com/terms-of-service"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline text-gray-800"
                >
                  Terms of Service
                </a>{' '}
                and{' '}
                <a
                  href="https://www.nexusbookai.com/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline text-gray-800"
                >
                  Privacy Policy
                </a>
                .
              </p>

              <button
                type="button"
                onClick={() => navigate('/signup-nexus')}
                className="mt-3 w-full text-center text-[13px] font-medium text-gray-500 hover:text-gray-800"
              >
                Back
              </button>
            </form>
          ) : (
            <form
              onSubmit={handlePasswordContinue}
              className="w-full rounded-2xl bg-white px-5 py-6 sm:px-7 sm:py-7 shadow-[0_8px_40px_rgba(0,0,0,0.35)] border border-white/80"
            >
              <p className="text-[13px] text-gray-500 mb-4">
                Creating your account for{' '}
                <span className="font-semibold text-gray-900">{email}</span>
              </p>

              <label
                className="block text-[13px] font-semibold text-gray-900 mb-1.5"
                htmlFor="nexus-password"
              >
                Password
              </label>
              <input
                id="nexus-password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (error) setError(null);
                }}
                className="w-full h-11 px-3 rounded-xl border border-gray-300 bg-white text-[15px] text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#3B82F6]/40 focus:border-[#3B82F6]"
              />
              <div className="mt-2">
                <PasswordRequirements password={password} variant="light" />
              </div>

              <label className="mt-4 flex items-start gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={marketingOptIn}
                  onChange={(e) => setMarketingOptIn(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-[#3B82F6] focus:ring-[#3B82F6]"
                />
                <span className="text-[12px] leading-snug text-gray-500">
                  By checking this box, you agree to receive marketing emails from Nexus (newsletters,
                  updates, offers, etc.). Unsubscribe at any time. View our{' '}
                  <a
                    href="https://www.nexusbookai.com/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline text-gray-800"
                  >
                    Privacy Policy
                  </a>
                  .
                </span>
              </label>

              {error && (
                <p className="mt-3 text-[13px] text-red-600 font-medium" role="alert">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="mt-5 w-full h-12 rounded-full text-white text-[16px] font-semibold transition-opacity hover:opacity-95 disabled:opacity-60"
                style={{ background: BRAND_GRADIENT, boxShadow: BRAND_CTA_SHADOW }}
              >
                {submitting ? 'Checking…' : 'Continue'}
              </button>

              <button
                type="button"
                onClick={() => {
                  setError(null);
                  setStep('email');
                }}
                className="mt-3 w-full text-center text-[13px] font-medium text-gray-500 hover:text-gray-800"
              >
                Back
              </button>
            </form>
          )}
        </div>

      </div>

      <style>{`
        .nexus-email-page {
          transition: transform 420ms cubic-bezier(0.22, 1, 0.36, 1),
            opacity 320ms ease-out;
          will-change: transform, opacity;
        }
        @media (prefers-reduced-motion: reduce) {
          .nexus-email-page {
            transition: none !important;
            opacity: 1 !important;
            transform: none !important;
          }
        }
      `}</style>
    </div>
  );
}
